/**
 * OfflineIndicator — a fixed, globally-visible pill showing connectivity + the
 * offline outbox state, expandable into a sync panel. Rendered once from
 * AppLayout so it follows the user across every authed route.
 *
 *   offline            → "Offline" (amber)
 *   online + N queued  → "N to sync" (tap to open the panel / force a drain)
 *   online + empty     → hidden
 */
import { useState } from 'react'
import { CloudOff, RefreshCw, AlertTriangle, X } from 'lucide-react'
import { useOnlineStatus, useOutbox, drainQueue, retryFailed, clearFailed } from '../../lib/offlineSync'
import type { QueuedOp } from '../../lib/offlineQueue'

const STATUS_COLOR: Record<QueuedOp['status'], string> = {
  pending: 'var(--color-primary)',
  syncing: '#60A5FA',
  failed: '#E5533C',
}

export function OfflineIndicator() {
  const online = useOnlineStatus()
  const { ops, count } = useOutbox()
  const [open, setOpen] = useState(false)

  const failed = ops.filter((o) => o.status === 'failed').length

  // Nothing to surface when we're online with an empty outbox.
  if (online && count === 0) return null

  const label = !online
    ? `Offline${count > 0 ? ` · ${count} to sync` : ''}`
    : failed > 0
      ? `${failed} failed to sync`
      : `${count} to sync`
  const color = !online || failed > 0 ? 'var(--color-primary)' : '#4ADE80'

  return (
    <>
      {open && (
        <div
          role="dialog"
          aria-label="Offline sync"
          style={{
            position: 'fixed',
            bottom: 'calc(58px + env(safe-area-inset-bottom, 0px))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 71,
            width: 'min(360px, 92vw)',
            maxHeight: '50vh',
            overflowY: 'auto',
            background: 'var(--color-bg-card)',
            border: '1px solid var(--color-border)',
            borderRadius: 14,
            boxShadow: '0 8px 28px rgba(0,0,0,0.28)',
            padding: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-text)' }}>
              {online ? 'Pending sync' : 'Offline — saved on this device'}
            </div>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', color: 'var(--color-text-faint)', cursor: 'pointer', display: 'flex' }}><X size={15} /></button>
          </div>

          {count === 0 ? (
            <div style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>All changes synced.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ops.map((op) => (
                <div key={op.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 12.5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: 99, background: STATUS_COLOR[op.status], marginTop: 5, flexShrink: 0 }} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ color: 'var(--color-text)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{op.label}</div>
                    {op.status === 'failed' && op.error && (
                      <div style={{ color: '#E5533C', fontSize: 11 }}>{op.error}</div>
                    )}
                  </div>
                  <span style={{ color: 'var(--color-text-faint)', fontSize: 11, flexShrink: 0 }}>{op.status}</span>
                </div>
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            {online && count > 0 && (
              <button onClick={() => void drainQueue()} style={panelBtn('primary')}>Sync now</button>
            )}
            {failed > 0 && <button onClick={() => void retryFailed()} style={panelBtn()}>Retry failed</button>}
            {failed > 0 && <button onClick={() => void clearFailed()} style={panelBtn('danger')}>Clear failed</button>}
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        title={online ? 'Tap for sync details' : 'Will sync when you reconnect'}
        aria-label={label}
        style={{
          position: 'fixed',
          bottom: 'calc(14px + env(safe-area-inset-bottom, 0px))',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 70,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 7,
          padding: '8px 14px',
          borderRadius: 99,
          fontSize: 12.5,
          fontWeight: 700,
          color,
          background: 'var(--color-bg-card)',
          border: `1px solid color-mix(in srgb, ${color} 45%, transparent)`,
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
          cursor: 'pointer',
        }}
      >
        {!online ? <CloudOff size={14} /> : failed > 0 ? <AlertTriangle size={14} /> : <RefreshCw size={14} />}
        {label}
      </button>
    </>
  )
}

function panelBtn(variant?: 'primary' | 'danger'): React.CSSProperties {
  return {
    padding: '7px 12px',
    borderRadius: 8,
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    border: `1px solid ${variant === 'danger' ? 'color-mix(in srgb, #E5533C 45%, transparent)' : 'var(--color-border)'}`,
    background: variant === 'primary' ? 'var(--color-primary)' : 'transparent',
    color: variant === 'primary' ? 'var(--color-bg)' : variant === 'danger' ? '#E5533C' : 'var(--color-text)',
  }
}
